import React, { useContext } from 'react';
import { MainContext } from '../../context/MainContext';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import MemoryIcon from '@mui/icons-material/Memory';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import WifiFindIcon from '@mui/icons-material/WifiFind';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import LanIcon from '@mui/icons-material/Lan';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SignalWifiConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiConnectedNoInternet4';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

import * as Constants from '../api/constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


function createRow(id, label, icon) {
  return { id, label, icon, value: "N/A" };
}

export default function HistorySNWifiR2() {
  const state = useContext(MainContext).state;
  const sn = state.snWifiR2?.split(',') ?? [];
  const deviceId = state.selectedDevice;

  const ICON_COLORS = {
    heat: "#FF7043",        // orange / heat
    cool: "#4FC3F7",        // light blue
    water: "#29B6F6",       // pool / water
    time: "#BA68C8",        // timers
    power: "#FFD54F",       // electrical
    speed: "#81C784",       // motion / speed
    level: "#90A4AE",       // neutral / measurement
    error: "#FF0000",
    bluetooth: "#0a3d91",
    wifi: "#D3D3D3",
    mqtt: "#660066"
  };

  // Row definitions with matching icons
  const rows = [
    createRow(0, 'Command Echo', <SettingsBackupRestoreIcon sx={{ color: ICON_COLORS.level }} />),
    createRow(1, 'Controller Model', <DeveloperBoardIcon sx={{ color: ICON_COLORS.speed }} />),
    createRow(2, 'Controller Software Version', <MemoryIcon sx={{ color: ICON_COLORS.water }} />),
    createRow(3, 'SSIG Detect Enabled', <WifiFindIcon sx={{ color: ICON_COLORS.wifi }} />),
    createRow(4, 'SSIG Found', <WifiTetheringIcon sx={{ color: ICON_COLORS.mqtt }} />),
    createRow(5, 'SSIG Connected', <LanIcon sx={{ color: ICON_COLORS.water }} />),
    createRow(6, 'SSIG IP Address', <LanIcon sx={{ color: ICON_COLORS.water }} />),
    createRow(7, 'SSIG Host Address', <LanIcon sx={{ color: ICON_COLORS.water }} />),
    createRow(8, 'SSIG Connection Up time', <AccessTimeIcon sx={{ color: ICON_COLORS.time }} />),
    createRow(9, 'SSIG Fail Counter', <SignalWifiConnectedNoInternet4Icon sx={{ color: ICON_COLORS.wifi }} />),
    createRow(10, 'SSIG Last Error', <ErrorOutlineIcon sx={{ color: ICON_COLORS.power }} />),
    createRow(11, 'Heat Signal', <LocalFireDepartmentIcon sx={{ color: ICON_COLORS.heat }} />),
  ];

  // Fill values
  rows.forEach((row, index) => {
    row.value = sn[index] ?? "N/A";
  });

  const sendCommand = (command, value) => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ DeviceId: deviceId, Command: command, Value: value }),
      credentials: "include"
    };

    fetch(Constants.baseURL + Constants.apiSendCommand, requestOptions)
      .then((res) => {
        if (res.ok) {
          toast.success('Updated successfully. Refreshing...', { autoClose: 7000 });

          setTimeout(() => state.refreshDeviceRF(deviceId), 3000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 9000);
        } else {
          toast.error('Update failed', { autoClose: 5000 });
        }
      })
      .catch(err => console.log("request failed", err));
  }

  const handleUpdate = () => sendCommand('SN-WIFI-R2', '');
  const handleToggle = () => {
    const enabled = sn[3] === "1";
    sendCommand('SN-WIFI-SSIG:', enabled ? "0" : "1");
  };

  return (
    <>
      <ToastContainer />

      <Typography
        component="h2"
        variant="h6"
        sx={{ color: "white", fontWeight: 600, mb: 2 }}
      >
        SN-WIFI-R2 Data
      </Typography>

      <Grid container spacing={1.5}>
        {rows.map((item) => (
          <Grid item xs={12} md={6} key={item.id}>
            <Paper
              sx={{
                p: 1.8,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "0.2s ease",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.08)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {/* Icon bubble */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                }}
              >
                {item.icon}
              </Box>

              {/* Label + Value */}
              <Box sx={{
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.3,
                mt: 0.4
              }}>
                <Typography
                  sx={{
                    color: "white",
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  {item.label}
                </Typography>

                {/* Value chip */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 13,
                    color: 'text.disabled',
                  }}
                >
                  {item.value ? item.value : '-'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* Update button tile */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleUpdate}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.4)",
                px: 4,
                textTransform: "none",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Update
            </Button>
          </Paper>
        </Grid>

        {/* Toggle On/Off tile */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleToggle}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.4)",
                px: 4,
                textTransform: "none",
                "&:hover": {
                  borderColor: "white",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {sn[3] === "1" ? "Turn Off" : "Turn On"}
            </Button>
          </Paper>
        </Grid>

      </Grid>
    </>
  );
}
